import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import ar from './locales/ar.json';
import he from './locales/he.json';

export const SUPPORTED_LANGUAGES = ['en', 'ar', 'he'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const RTL_LANGUAGES: SupportedLanguage[] = ['ar', 'he'];

export const isRtlLanguage = (lang: string): boolean =>
    RTL_LANGUAGES.includes(normalizeLanguage(lang));

export const normalizeLanguage = (lang: string): SupportedLanguage => {
    const base = lang.split('-')[0] as SupportedLanguage;
    if (SUPPORTED_LANGUAGES.includes(base)) {
        return base;
    }
    return 'en';
};

export const applyDocumentLanguage = (lang: SupportedLanguage): void => {
    document.documentElement.lang = lang;
    document.documentElement.dir = isRtlLanguage(lang) ? 'rtl' : 'ltr';
};

const STORAGE_KEY = 'marrygo-lang';

const getInitialLanguage = (): SupportedLanguage => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
        return normalizeLanguage(stored);
    }
    const browserLang = navigator.language;
    if (browserLang) {
        return normalizeLanguage(browserLang);
    }
    return 'en';
};

const initialLanguage = getInitialLanguage();
applyDocumentLanguage(initialLanguage);

i18n.use(initReactI18next).init({
    resources: {
        en: { translation: en },
        ar: { translation: ar },
        he: { translation: he },
    },
    lng: initialLanguage,
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
});

i18n.on('languageChanged', (lang) => {
    const normalized = normalizeLanguage(lang);
    if (SUPPORTED_LANGUAGES.includes(normalized)) {
        localStorage.setItem(STORAGE_KEY, normalized);
        applyDocumentLanguage(normalized);
    }
});

export const getStoredLanguage = (): SupportedLanguage => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? normalizeLanguage(stored) : 'en';
};

export default i18n;
